import { z } from "zod";
import { apiRegistrationSchema } from "./api-access";
export * from "./api-access";
export * from "./costs";
export * from "./activity-tracker";
export * from "./runtime-profiles";

export const appStatuses = [
  "draft",
  "building",
  "pending_approval",
  "approved",
  "rejected",
  "suspended",
  "archived",
] as const;
export type AppStatus = (typeof appStatuses)[number];

export const buildStatuses = ["queued", "building", "failed", "ready", "promoted", "rolled_back"] as const;
export type BuildStatus = (typeof buildStatuses)[number];

export const permissionNames = [
  "camera",
  "microphone",
  "geolocation",
  "clipboardRead",
  "clipboardWrite",
  "popups",
  "fullscreen",
  "userDirectory",
] as const;
export type PermissionName = (typeof permissionNames)[number];

export const permissionsSchema = z.object({
  camera: z.boolean().default(false),
  microphone: z.boolean().default(false),
  geolocation: z.boolean().default(false),
  clipboardRead: z.boolean().default(false),
  clipboardWrite: z.boolean().default(false),
  popups: z.boolean().default(false),
  fullscreen: z.boolean().default(true),
  userDirectory: z.boolean().default(false),
});

const repositoryPath = z.string().min(1).refine((value) => !value.startsWith("/") && !value.split(/[\\/]/).includes("..") && !value.includes("\0"), {
  message: "Repository paths must stay inside the checked-out source tree",
});

export const cheesyAppManifestSchema = z.object({
  schemaVersion: z.literal(1),
  runtime: z.object({
    dockerfile: repositoryPath.default("Dockerfile"),
    context: repositoryPath.default("."),
    healthPath: z.string().startsWith("/")
      .refine((value) => !value.startsWith("/_cheesy/"), { message: "The /_cheesy/ namespace is reserved by Cheesy Apps" })
      .refine((value) => !value.toLowerCase().endsWith("z"), { message: "Cloud Run reserves some paths ending in z; use a path such as /health" }),
  }),
  permissions: permissionsSchema,
  api: apiRegistrationSchema.optional(),
});

export type CheesyAppManifest = z.infer<typeof cheesyAppManifestSchema>;
export type AppPermissions = z.infer<typeof permissionsSchema>;

export interface CatalogApp {
  mobileSupported: boolean;
  id: string;
  slug: string;
  name: string;
  summary: string;
  description: string;
  status: AppStatus;
  categories: string[];
  tags: string[];
  owners: string[];
  icon?: string;
  permissions: AppPermissions;
  updatedAt: number;
  liveVersion?: string;
  repository?: string;
}

export interface ReleaseSummary {
  id: string;
  appId: string;
  commitSha: string;
  imageDigest: string;
  status: BuildStatus;
  createdAt: number;
  promotedAt?: number;
  scan: { critical: number; high: number; secrets: number };
}

export interface BuildSummary {
  id: string;
  appId: string;
  commitSha: string;
  status: BuildStatus;
  createdAt: number;
  completedAt?: number;
  logUrl?: string;
  failureMessage?: string;
  scan: { critical: number; high: number; secrets: number };
  findings: Array<{ id: string; severity: "low" | "medium" | "high" | "critical" | "secret"; code: string; summary: string; overridden: boolean; overrideReason?: string }>;
}

export interface AppManagementSummary {
  appId: string;
  owners: Array<{ id: string; name: string; email: string }>;
  repository?: { fullName: string; branch: string; connected: boolean };
  secrets: Array<{ name: string; version: number; updatedAt: number }>;
}

export interface CheesyIdentityClaims {
  iss: string;
  aud: string;
  sub: string;
  email: string;
  name: string;
  appRole: "owner" | "user";
  platformRole: "admin" | "user";
  iat: number;
  exp: number;
  jti: string;
}

export const reservedPlatformPrefix = "/_cheesy/";

export function normalizeEmail(email: string): string {
  return email.trim().toLocaleLowerCase("en-US");
}

export async function manifestFingerprint(manifest: CheesyAppManifest): Promise<string> {
  const normalized = JSON.stringify(cheesyAppManifestSchema.parse(manifest));
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normalized));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function iframeSandbox(permissions: AppPermissions): string {
  const values = [
    "allow-scripts",
    "allow-forms",
    "allow-same-origin",
    "allow-downloads",
    "allow-top-navigation-by-user-activation",
  ];
  if (permissions.popups) values.push("allow-popups");
  return values.join(" ");
}

export function iframeAllow(permissions: AppPermissions): string {
  const mapping: Array<[PermissionName, string]> = [
    ["camera", "camera"],
    ["microphone", "microphone"],
    ["geolocation", "geolocation"],
    ["clipboardRead", "clipboard-read"],
    ["clipboardWrite", "clipboard-write"],
    ["fullscreen", "fullscreen"],
  ];
  return mapping.filter(([key]) => permissions[key]).map(([, value]) => `${value} 'self'`).join("; ");
}
export * from "./feedback";
export * from "./platform-api";
